import type { ComplaintCategory } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { COMPLAINT_CATEGORY_KEYS } from './complaint-labels'

type IProps = {
  busy: boolean
  onCancel: () => void
  onCreate: (input: { category: ComplaintCategory, subject: string, message: string }) => void
}

/** Saisir une réclamation reçue hors de l'app : courrier, téléphone, entretien (#325). */
function NewComplaintDialog({ busy, onCancel, onCreate }: IProps) {
  const { t } = useTranslation()
  const [category, setCategory] = useState<ComplaintCategory | ''>('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const valid = category && subject.trim() && message.trim()

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (category && valid) onCreate({ category, subject, message })
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('complaints.new.title')}</DialogTitle>
            <DialogDescription>{t('complaints.new.description')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="newComplaintCategory" required>{t('complaints.column.category')}</Label>
            <select id="newComplaintCategory" value={category} required onChange={e => setCategory(e.target.value as ComplaintCategory)} className="h-10 rounded-md border border-input bg-transparent px-3 text-sm">
              <option value="" disabled>{t('complaints.categoryPlaceholder')}</option>
              {Object.entries(COMPLAINT_CATEGORY_KEYS).map(([value, key]) => <option key={value} value={value}>{t(key)}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="newComplaintSubject" required>{t('complaints.column.subject')}</Label>
            <Input id="newComplaintSubject" maxLength={150} required value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="newComplaintMessage" required>{t('complaints.column.message')}</Label>
            <textarea id="newComplaintMessage" rows={5} maxLength={5000} required value={message} onChange={e => setMessage(e.target.value)} className="rounded-md border border-input bg-transparent px-3 py-2 text-sm" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!valid || busy}>{t('complaints.new.submit')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default NewComplaintDialog
